import { formatNumber } from "pages/MatchDetail/MatchedOdds";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./style.css";

interface FancyPLData {
  sport_name: string;
  uplineProfitLoss: number;
  total: number;
  commission: number;
  type: number;
  sport_id: string;
  total_net_pl: string;
  series_name: string;
  series_id: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  total: number;
  onClickRow?: (matchId: string, matchName: string) => void;
  searchTerm: string; // Search term controlled by parent
}

const EventReportTable: React.FC<FancyPLTableProps> = ({
  fancyPLData,
  total,
  onClickRow,
  searchTerm, // Search term passed as prop
}) => {
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filter data based on the search term
  const filteredData = fancyPLData.filter(
    (item) =>
      item.sport_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.series_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle sort toggle for column header
  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      // Toggle sorting order if the same column is clicked
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Set new sort key and default to ascending order
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  // Determine the correct sorting icons for each column
  const getSortClass = (key: keyof FancyPLData) => {
    const isActive = sortKey === key;
    const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
    const downClass = isActive && sortOrder === "desc" ? "text-dark" : "text-muted";

    return (
      <span className="d-flex flex-column ms-2">
        <i className={`bi bi-caret-up-fill ${upClass}`} style={{ lineHeight: 0.6 }} />
        <i className={`bi bi-caret-down-fill ${downClass}`} style={{ lineHeight: 0.6 }} />
      </span>
    );
  };

  // Sort filtered data based on the selected column and order
  const sortedData = sortKey
    ? [...filteredData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];

        // Sorting for string values
        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        // Sorting for numeric values
        const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
        const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      })
    : filteredData;

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th onClick={() => handleSort("sport_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name")}
              </div>
            </th>
            <th onClick={() => handleSort("series_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Series Name</span>
                {getSortClass("series_name")}
              </div>
            </th>
            <th onClick={() => handleSort("total_net_pl")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Total Net PL</span>
                {getSortClass("total_net_pl")}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr
              key={index}
              onClick={() => onClickRow?.(String(item.series_id), item.series_name)}
              style={{ cursor: "pointer" }}
            >
              <td>{item.sport_name}</td>
              <td>{item.series_name}</td>
              <td className={Number(item.total_net_pl) < 0 ? "text-danger" : "text-success"}>
                {formatNumber(item.total_net_pl)}
              </td>
            </tr>
          ))}
          <tr>
            <td><strong>Total</strong></td>
            <td></td>
            <td className={Number(total) < 0 ? "text-danger" : "text-success"}>
              <strong>{formatNumber(total.toString())}</strong>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

export default EventReportTable;
